import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signUp as authSignUp,
  signIn,
  signOut,
  resetPassword as authResetPassword,
  onAuthStateChange,
} from '../services/auth.service';
import { usersService, isMockMode } from '../services/service-proxy';
import { demoConfig } from '../config/environment';
import logger from '../utils/logger.util';

const {
  createUserProfile,
  getUserProfile,
  updateUserProfile: updateProfile,
} = usersService;

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  error: null,
  isDemo: false,
  login: () => {},
  loginAsDemo: () => {},
  signup: () => {},
  logout: () => {},
  resetPassword: () => {},
  updateUserProfile: () => {},
  clearError: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch the Firestore profile for a given Firebase user.
   * @param {import('firebase/auth').User} firebaseUser
   * @returns {Promise<Object|null>}
   */
  const fetchProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return null;
    try {
      const userProfile = await getUserProfile(firebaseUser.uid);
      return userProfile;
    } catch (err) {
      logger.error('Failed to fetch profile:', err);
      return null;
    }
  }, []);

  // Subscribe to Firebase auth state changes - single source of truth
  // In mock mode, bypass Firebase auth and inject mock user immediately.
  useEffect(() => {
    if (isMockMode) {
      const { MOCK_CURRENT_USER, MOCK_CURRENT_USER_ID } = require('../data/mock/mock-users.data');
      setUser({ uid: MOCK_CURRENT_USER_ID, email: MOCK_CURRENT_USER.email });
      setProfile(MOCK_CURRENT_USER);
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const userProfile = await fetchProfile(firebaseUser);
        setProfile(userProfile);
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchProfile]);

  /**
   * Sign up a new user with email/password and create a Firestore profile.
   * @param {Object} userData - The signup form data.
   */
  const signup = async (userData) => {
    setError(null);
    try {
      const credential = await authSignUp(
        userData.email,
        userData.password,
        userData.displayName
      );

      await createUserProfile(credential.user.uid, {
        displayName: userData.displayName,
        username: userData.username.toLowerCase(),
        email: userData.email,
        birthdate: userData.birthdate || null,
        gender: userData.gender || '',
      });

      // Profile will be loaded by onAuthStateChanged listener
      // New users go to onboarding wizard
      navigate('/onboarding');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Log in an existing user with email and password.
   * @param {string} email
   * @param {string} password
   */
  const login = async (email, password) => {
    setError(null);
    try {
      await signIn(email, password);
      // Profile will be loaded by onAuthStateChanged listener
      navigate('/home');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Log in as the demo account for recruiter preview.
   * Uses credentials from environment variables.
   * Enables mock data and does a full page reload so the service proxy
   * picks up mock services at module load time.
   */
  const loginAsDemo = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!demoConfig.email || !demoConfig.password) {
        throw new Error('Demo mode is not configured.');
      }
      await signIn(demoConfig.email, demoConfig.password);
      // Enable mock data for demo account and full-reload so service-proxy re-evaluates
      localStorage.setItem('fitsocial_demo_mock', 'true');
      window.location.href = '/home';
    } catch (err) {
      setError('Unable to start demo. Please try again later.');
      logger.error('Demo login failed:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Whether the current user is the demo account.
   */
  const isDemo = !!(user && demoConfig.email && user.email === demoConfig.email);

  /**
   * Log out the current user.
   * Clears demo mock flag if active and does a full reload to restore real services.
   */
  const logout = async () => {
    setError(null);
    try {
      const wasDemoMock = localStorage.getItem('fitsocial_demo_mock') === 'true';
      await signOut();
      if (wasDemoMock) {
        localStorage.removeItem('fitsocial_demo_mock');
        window.location.href = '/';
        return;
      }
      // State will be cleared by onAuthStateChanged listener
      navigate('/');
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Send a password reset email.
   * @param {string} email
   */
  const resetPassword = async (email) => {
    setError(null);
    try {
      await authResetPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Update the current user's Firestore profile.
   * @param {Object} updates - Fields to update.
   */
  const updateUserProfile = async (updates) => {
    setError(null);
    try {
      if (!user) throw new Error('You must be logged in to update your profile.');
      const updatedProfile = await updateProfile(user.uid, updates);
      setProfile(updatedProfile);
      return updatedProfile;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    isDemo,
    login,
    loginAsDemo,
    signup,
    logout,
    resetPassword,
    updateUserProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext;
